import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  buildDynamicsEnvironmentLoginUrl,
  clearDynamicsConnectError,
  clearDynamicsOauthFlow,
  getDynamicsConnectError,
  getDynamicsConnectUrl,
  getDynamicsOauthFlow,
} from 'hooks/services/dynamics/dynamicsOauthState';
import { DynamicsEnvironment } from 'hooks/services/dynamics/useDynamicsEnvironments';

/**
 * The page-side half of the Dynamics connect: starts sign-in 1 from the
 * current page (the callback brings the user back here), and exposes what the
 * callback left behind — the environments to pick from, or why it failed.
 */
const useDynamicsConnectFlow = () => {
  const { pathname, search } = useLocation();
  const [flow, setFlow] = useState(getDynamicsOauthFlow);
  const [connectError] = useState(getDynamicsConnectError);
  const environmentsToPick = flow?.step === 'picking' ? flow : null;

  // The banner explains the attempt the user just came back from; a later
  // visit to the page starts clean.
  useEffect(() => {
    clearDynamicsConnectError();
  }, []);

  const startConnect = () =>
    window.location.replace(getDynamicsConnectUrl(`${pathname}${search}`));

  const cancelPicking = () => {
    clearDynamicsOauthFlow();
    setFlow(null);
  };

  const connectEnvironment = (environment: DynamicsEnvironment) => {
    if (!environmentsToPick) return;
    window.location.replace(
      buildDynamicsEnvironmentLoginUrl(
        environmentsToPick.returnPath,
        environment,
      ),
    );
  };

  return {
    environmentsToPick: environmentsToPick?.environments ?? null,
    connectError,
    /** A connect is mid-way (picker) or has just failed on this page. */
    isPending: !!environmentsToPick || !!connectError,
    startConnect,
    cancelPicking,
    connectEnvironment,
  };
};

export default useDynamicsConnectFlow;
